import { rem } from 'polished'
import { memo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineHouseUserIcon from 'LuxKit/Icons/line/LineHouseUserIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineShowerIcon from 'LuxKit/Icons/line/LineShowerIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { pipeSeparatorMessage } from 'LuxKit/intl/separators'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferPageReviewAiSummary from 'components/OfferPage/NavigableOfferContent/OfferPageReviewAiSummary'
import OfferDescription from 'components/OfferPage/OfferDescription'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { Experiments } from 'constants/optimizely'
import { isOfferPageReviewAiSummaryDisabled } from 'constants/review'
import useExperiment from 'hooks/Optimizely/useExperiment'
import useUserReviewsSummary from 'hooks/Reviews/useUserReviewsSummary'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import PropertyTierProgressionPanel from 'loyalty/components/Banners/PropertyTierProgressionPanel'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'

import OfferPageHighlightTags from '../OfferPageHighlightTags'
import PropertyBedGroups from './PropertyBedGroups'
import PropertyPopularFacilities from './PropertyPopularFacilities'
import PropertyPromotionSummary from './PropertyPromotionSummary'

const OfferIntroSection = styled(BodyText)`
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const SingleUnitHighlights = styled.div`
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: repeat(2, 1fr);
  // When combined with parent's gap, this is effectively 20px. However, spacing/separation
  // consistency between desktop and mobile is kinda a mess, and needs to be reworked.
  margin-top: ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
    margin-bottom: ${rem(8)};
  }
`

const IconContainer = styled.div`
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.default.six};
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  height: ${rem(36)};
  justify-content: center;
  width: ${rem(36)};
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  propertyRates: Array<App.EnrichedPropertyRate>
  duration: number
  checkInDate?: string
  checkoutDate?: string
  isUltraLux: boolean
  showTasteOfGoldPromo?: boolean
  isOccupancyContextKnown: boolean
  rooms: Array<App.Occupants>
}

function PropertyPropertyDetails(props: Props) {
  const intl = useIntl()
  const {
    property,
    offer,
    propertyRates,
    duration,
    checkInDate,
    checkoutDate,
    isUltraLux,
    showTasteOfGoldPromo,
    isOccupancyContextKnown,
    rooms,
  } = props

  const location = property.location.title
  const sep = intl.formatMessage(pipeSeparatorMessage)
  const propertyLogoAlt = property.name
    ? intl.formatMessage({
        id: 'ybBusjTXR815C1wlVUim',
        defaultMessage: '{propertyName} logo',
        description: 'Alt text for a property logo image on the property details section of the offer page',
      }, { propertyName: property.name })
    : intl.formatMessage({
        id: 'Cr9ql3GjSlaTtl0HYYDd',
        defaultMessage: 'Vendor logo',
        description: 'Fallback alt text for a property logo image on the property details section of the offer page',
      })
  const isReviewAiSummaryDisabled = isOfferPageReviewAiSummaryDisabled(property.id)
  const { isAnyVariant: isHighlightReviewEnabled } = useExperiment(
    Experiments.croHighlightReviewAiSummary,
    { canActivate: !isReviewAiSummaryDisabled },
  )
  const shouldShowHighlightReviewSummary =
    isHighlightReviewEnabled &&
    !isReviewAiSummaryDisabled &&
    isOfferRatingDisplayable(property.rating)
  const [reviewSummary, fetchingReviewSummary] = useUserReviewsSummary(
    property.id,
    'luxuryescapes',
    {
      enabled: shouldShowHighlightReviewSummary,
      summaryType: 'highlight',
    },
  )
  const hasReviewSummaryContent = !!reviewSummary?.summary

  return (<>
    <HeaderSection>
      <Header>
        <VerticalSpacer gap={8}>
          <BodyTextBlock
            variant="small"
            data-testid="offer-location"
            startIcon={<LineMapMarkerIcon />}
          >
            {location && <FormattedMessage
              id="v_C61pq0a6_XdROVcmUY"
              defaultMessage="{location} {sep} <link>View on map</link>"
              description="Property location with link to view on map on the map view"
              values={{
                location,
                sep,
                link: (chunks) => <TextLink href="#getting-there">{chunks}</TextLink>,
              }}
            />}
            {!location && <TextLink href="#getting-there">{intl.formatMessage({
              id: 'AJC-wQ16MbO4N_EBndfu',
              defaultMessage: 'View on map',
              description: 'Link to view property location on map when no address is available',
            })}
            </TextLink>}
          </BodyTextBlock>
          <Heading variant="heading2" as="h1">{property.name}</Heading>
          <BodyText variant="large" as="h2">{property.headline}</BodyText>

          {!(shouldShowHighlightReviewSummary && hasReviewSummaryContent) && isOfferRatingDisplayable(property.rating) && <OfferRating
            link
            inlineLabel
            variant="small"
            rating={property.rating}
          />}
        </VerticalSpacer>
        {property.logo?.id &&
          <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logo.id}
            alt={propertyLogoAlt}
          />}
      </Header>
      {property.singleUnit && <SingleUnitHighlights>
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <IconContainer><LineHouseUserIcon size="S" /></IconContainer>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'N7g0StNAxiH5W_tKT-rT',
              defaultMessage: 'Entire home',
              description: 'Generic category label for single-unit non-hotel-accommodation on single property page',
            })}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <IconContainer><LineUsersAltIcon size="S" /></IconContainer>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'K6NBwjaeEG8KKEiHzzhY',
              defaultMessage: 'Sleeps {count}',
              description: 'Label for maximum number of non-infant occupants for single-unit non-hotel-accommodation on single property page',
            }, { count: property.singleUnit.maxNonInfantOccupants })}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <IconContainer><LineBedIcon size="S" /></IconContainer>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'IadUUi1ZtkXzc-YrlSF-',
              defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
              description: 'Label for number of bedrooms for single-unit non-hotel-accommodation on single property page',
            }, { count: property.singleUnit.bedrooms })}
          </BodyText>
        </Group>
        <Group direction="horizontal" gap={12} verticalAlign="center">
          <IconContainer><LineShowerIcon size="S" /></IconContainer>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '3hNEb313XnPgIlpMYOFz',
              defaultMessage: '{count, plural, one {# bathroom} other {# bathrooms}}',
              description: 'Label for number of bathrooms for single-unit non-hotel-accommodation on single property page',
            }, { count: property.singleUnit.bathrooms })}
          </BodyText>
        </Group>
      </SingleUnitHighlights>}
      {fetchingReviewSummary && shouldShowHighlightReviewSummary && <LoadingBox height={160} />}
      {reviewSummary?.summary && shouldShowHighlightReviewSummary && <OfferPageReviewAiSummary
        content={reviewSummary.summary}
        score={property.rating!.score}
        reviewsTotal={property.rating!.reviewsTotal}
      />}
      <OfferPageHighlightTags
        duration={duration}
        hasSelectedDates={!!checkInDate}
        isOccupancyContextKnown={isOccupancyContextKnown}
        propertyId={property.id}
        rooms={rooms}
      />
      <PropertyTierProgressionPanel
        property={property}
        rates={propertyRates}
        nights={duration}
        checkoutDate={checkoutDate}
        isUltraLux={isUltraLux}
      />
      {showTasteOfGoldPromo && <TasteOfGoldPromoCard>
        <CSSBreakpoint max="mobile">
          <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
        </CSSBreakpoint>
      </TasteOfGoldPromoCard>}
    </HeaderSection>
    {offer.badge && <OfferBadgeLabel badge={offer.badge} />}

    <OfferIntroSection variant="large">
      {property.description && <OfferDescription description={property.description} />}
      {property.additionalDescription && <MarkdownRender type="compact" fontSize="medium" content={property.additionalDescription} />}
      {property.singleUnit?.bedGroups && <PropertyBedGroups bedGroups={property.singleUnit.bedGroups} />}
      {/*
        * NB: If we don't have any popular facilities to display, we'll still show
        * facilities/amenities in `<PropertyNavigableOfferContent/>`.
        */}
      {property.popularFacilities.length > 0 && <PropertyPopularFacilities
        popularFacilities={property.popularFacilities}
        facilities={property.facilities}
        amenities={property.amenities}
      />}
      {/*
        * NB: We'll bring back LPC tactical promos in HP-2365 via accommodation service.
        *
        * For Marriott promos, after discussion with James Ball, SPP's implementation differs from
        * `<BedbankPropertyDetails/>` in that: (i) we don't hide popular facilities (for now); and
        * (ii) we show promos to all white labels.
        */}
      <PropertyPromotionSummary />
    </OfferIntroSection>
  </>)
}

export default memo(PropertyPropertyDetails)
